import type { Question } from "../data/questions";
import type { ProgressData, QuestionStat } from "./progress";

export interface PreparedOption { text: string; sourceIndex: number }
export interface PreparedQuestion { question: Question; options: PreparedOption[] }

function random(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] / 0x1_0000_0000;
}

export function shuffle<T>(values: readonly T[]): T[] {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

const ageBonus = (stat?: QuestionStat) => !stat?.lastSeen ? 0 : Math.min(1200, (Date.now() - stat.lastSeen) / 3_600_000);

export function selectQuestions(bank: Question[], count: number, progress: ProgressData): Question[] {
  const recent = new Set(progress.recent.slice(0, Math.max(15, count)));
  const ranked = bank.map((question) => {
    const stat = progress.stats[question.id];
    const unseen = !stat?.seen;
    const weak = !!stat && stat.wrong > 0 && stat.streak < 3;
    const score =
      (unseen ? 100_000 : 0) +
      (weak ? 25_000 + stat.wrong * 500 - stat.streak * 1500 : 0) +
      ageBonus(stat) -
      (recent.has(question.id) && !weak ? 10_000 : 0) +
      random() * 100;
    return { question, score, tier: unseen ? 3 : weak ? 2 : 1 };
  });
  const ordered = ranked.sort((a, b) => b.score - a.score);
  const selected: Question[] = [];
  const usedIds = new Set<string>();
  const usedFamilies = new Set<string>();

  for (const tier of [3, 2, 1]) {
    const tierItems = ordered.filter((item) => item.tier === tier);
    for (const { question } of tierItems) {
      if (selected.length >= Math.min(count, bank.length)) break;
      if (usedFamilies.has(question.familyId)) continue;
      selected.push(question);
      usedIds.add(question.id);
      usedFamilies.add(question.familyId);
    }
    for (const { question } of tierItems) {
      if (selected.length >= Math.min(count, bank.length)) break;
      if (usedIds.has(question.id)) continue;
      selected.push(question);
      usedIds.add(question.id);
    }
    if (selected.length >= Math.min(count, bank.length)) break;
  }

  return shuffle(selected);
}

export function prepareQuestions(questions: Question[]): PreparedQuestion[] {
  return questions.map((question) => ({
    question,
    options: shuffle(question.choices.map((text, sourceIndex) => ({ text, sourceIndex }))),
  }));
}
